import React, { useEffect, forwardRef, useImperativeHandle, useState, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import Superscript from "@tiptap/extension-superscript";
import Subscript from "@tiptap/extension-subscript";
import Highlight from "@tiptap/extension-highlight";
import { Node, Mark, mergeAttributes } from "@tiptap/core";
import "./ClinicalEditor.scss";

/* ═══════════════════════════════════════════════════════════════════════
   Custom Nodes for Template Preservation
   ═══════════════════════════════════════════════════════════════════════ */

const DivNode = Node.create({
  name: "div",
  group: "block",
  content: "block*",
  parseHTML() {
    return [{ tag: "div" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes), 0];
  },
  addAttributes() {
    return {
      class: { default: null },
      style: { default: null },
      id: { default: null },
      "data-field": { default: null },
      "data-ph": { default: null },
    };
  },
});

const SpanNode = Node.create({
  name: "span",
  group: "inline",
  inline: true,
  content: "inline*",
  parseHTML() {
    return [{ tag: "span" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes), 0];
  },
  addAttributes() {
    return {
      class: { default: null },
      style: { default: null },
      id: { default: null },
      "data-field": { default: null },
      "data-ph": { default: null },
    };
  },
});

// Block headings (h1–h3) always restyle the whole paragraph. For partial
// selections, apply this mark so only the highlighted text picks up heading size.
const InlineHeading = Mark.create({
  name: "inlineHeading",
  inclusive: true,
  addAttributes() {
    return {
      level: {
        default: 1,
        parseHTML: (element) =>
          Number(element.getAttribute("data-inline-heading")) || 1,
      },
    };
  },
  parseHTML() {
    return [1, 2, 3].map((level) => ({
      tag: `span[data-inline-heading="${level}"]`,
      attrs: { level },
    }));
  },
  renderHTML({ mark }) {
    const level = mark.attrs.level || 1;
    return [
      "span",
      {
        "data-inline-heading": level,
        class: `inline-h${level}`,
      },
      0,
    ];
  },
});

function isFullBlockSelected(editor) {
  const { from, to, empty } = editor.state.selection;
  if (empty) return true;
  const { $from, $to } = editor.state.selection;
  if (!$from.sameParent($to)) return false;
  return from === $from.start() && to === $from.end();
}

function isHeadingLevelActive(editor, level) {
  return (
    editor.isActive("heading", { level }) ||
    editor.isActive("inlineHeading", { level })
  );
}

function applyHeadingLevel(editor, level) {
  if (!editor) return;
  if (isFullBlockSelected(editor)) {
    editor.chain().focus().toggleHeading({ level }).run();
    return;
  }
  if (editor.isActive("inlineHeading", { level })) {
    editor.chain().focus().unsetMark("inlineHeading").run();
    return;
  }
  editor
    .chain()
    .focus()
    .unsetMark("inlineHeading")
    .setMark("inlineHeading", { level })
    .run();
}

/* ═══════════════════════════════════════════════════════════════════════
   SVG Icons — lightweight inline icons to avoid external icon dependency
   ═══════════════════════════════════════════════════════════════════════ */

const Icon = ({ children, ...props }) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" {...props}>
    {children}
  </svg>
);

const BoldIcon = () => (
  <Icon><path d="M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z" /></Icon>
);
const ItalicIcon = () => (
  <Icon><path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z" /></Icon>
);
const UnderlineIcon = () => (
  <Icon><path d="M12 17c3.31 0 6-2.69 6-6V3h-2.5v8c0 1.93-1.57 3.5-3.5 3.5S8.5 12.93 8.5 11V3H6v8c0 3.31 2.69 6 6 6zm-7 2v2h14v-2H5z" /></Icon>
);
const StrikethroughIcon = () => (
  <Icon><path d="M10 19h4v-3h-4v3zM5 4v3h5v3h4V7h5V4H5zM3 14h18v-2H3v2z" /></Icon>
);
const BulletListIcon = () => (
  <Icon><path d="M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z" /></Icon>
);
const OrderedListIcon = () => (
  <Icon><path d="M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1zm5-6v2h14V5H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z" /></Icon>
);
const IndentIcon = () => (
  <Icon><path d="M3 21h18v-2H3v2zM3 8v8l4-4-4-4zm8 9h10v-2H11v2zM3 3v2h18V3H3zm8 6h10V7H11v2zm0 4h10v-2H11v2z" /></Icon>
);
const OutdentIcon = () => (
  <Icon><path d="M11 17h10v-2H11v2zm-8-5 4 4V8l-4 4zm0 9h18v-2H3v2zM3 3v2h18V3H3zm8 6h10V7H11v2zm0 4h10v-2H11v2z" /></Icon>
);
const SuperscriptIcon = () => (
  <Icon><path d="M22 7h-2v1h-1V7h-2V6h2V5h1v1h2v1zM2 19h14v-2H2v2zm0-6h14v-2H2v2z" /></Icon>
);
const SubscriptIcon = () => (
  <Icon><path d="M22 18h-2v1h-1v-1h-2v-1h2v-1h1v1h2v1zM2 19h14v-2H2v2zm0-6h14v-2H2v2z" /></Icon>
);
const HighlightIcon = () => (
  <Icon><path d="M12 4l-8 8v4h4l8-8-4-4zm-4 10l-2-2 6-6 2 2-6 6zM22 20H2v-2h20v2z" /></Icon>
);
const AlignLeftIcon = () => (
  <Icon><path d="M15 15H3v2h12v-2zm0-8H3v2h12V7zM3 13h18v-2H3v2zm0 8h18v-2H3v2zM3 3v2h18V3H3z" /></Icon>
);
const AlignCenterIcon = () => (
  <Icon><path d="M7 15v2h10v-2H7zM3 13h18v-2H3v2zm0 8h18v-2H3v2zm4-16v2h10V5H7zM3 9h18V7H3v2z" /></Icon>
);
const AlignRightIcon = () => (
  <Icon><path d="M3 21h18v-2H3v2zm6-4h12v-2H9v2zm-6-4h18v-2H3v2zm6-4h12V7H9v2zM3 3v2h18V3H3z" /></Icon>
);
const AlignJustifyIcon = () => (
  <Icon><path d="M3 21h18v-2H3v2zm0-4h18v-2H3v2zm0-4h18v-2H3v2zm0-4h18V7H3v2zM3 3v2h18V3H3z" /></Icon>
);
const TableIcon = () => (
  <Icon><path d="M20 2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM8 20H4v-4h4v4zm0-6H4v-4h4v4zm0-6H4V4h4v4zm6 12h-4v-4h4v4zm0-6h-4v-4h4v4zm0-6h-4V4h4v4zm6 12h-4v-4h4v4zm0-6h-4v-4h4v4zm0-6h-4V4h4v4z" /></Icon>
);
const HrIcon = () => (
  <Icon><rect x="2" y="11" width="20" height="2" rx="1" /></Icon>
);
const PageBreakIcon = () => (
  <Icon title="Insert Page Break"><path d="M4 2h16c1.1 0 2 .9 2 2v4h-2V4H4v4H2V4c0-1.1.9-2 2-2zm0 18h16c1.1 0 2-.9 2-2v-4h-2v4H4v-4H2v4c0 1.1.9 2 2 2zm-2-9h4v2H2v-2zm6 0h4v2H8v-2zm6 0h4v2h-4v-2zm6 0h2v2h-2v-2z" /></Icon>
);
const UndoIcon = () => (
  <Icon><path d="M12.5 8c-2.65 0-5.05 1.04-6.83 2.73L3 8v9h9l-2.73-2.73C10.69 12.85 12.48 12 14.5 12c3.04 0 5.61 1.93 6.58 4.63l1.89-.63C21.68 12.18 17.39 8 12.5 8z" /></Icon>
);
const RedoIcon = () => (
  <Icon><path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.05-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z" /></Icon>
);
const ChevronDownIcon = () => (
  <Icon viewBox="0 0 20 20"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" /></Icon>
);

/* ═══════════════════════════════════════════════════════════════════════
   Toolbar Component
   ═══════════════════════════════════════════════════════════════════════ */

const ToolbarButton = ({ onClick, isActive, disabled, title, children }) => (
  <button
    type="button"
    className={`clinical-editor__btn ${isActive ? "is-active" : ""}`}
    onClick={onClick}
    disabled={disabled}
    title={title}
  >
    {children}
  </button>
);

const TableMenu = ({ editor }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAction = (action) => {
    action();
    setIsOpen(false);
  };

  return (
    <div className="clinical-editor__table-menu" ref={menuRef}>
      <button
        type="button"
        className="clinical-editor__btn"
        onClick={() => setIsOpen(!isOpen)}
        title="Table"
      >
        <TableIcon />
        <ChevronDownIcon />
      </button>
      {isOpen && (
        <div className="clinical-editor__table-dropdown">
          <button onClick={() => handleAction(() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run())}>
            Insert 3×3 Table
          </button>
          <button onClick={() => handleAction(() => editor.chain().focus().insertTable({ rows: 2, cols: 2, withHeaderRow: true }).run())}>
            Insert 2×2 Table
          </button>
          <button onClick={() => handleAction(() => editor.chain().focus().insertTable({ rows: 4, cols: 5, withHeaderRow: true }).run())}>
            Insert 4×5 Table
          </button>
          {editor.can().addColumnAfter() && (
            <>
              <button onClick={() => handleAction(() => editor.chain().focus().addColumnAfter().run())}>
                Add Column After
              </button>
              <button onClick={() => handleAction(() => editor.chain().focus().addColumnBefore().run())}>
                Add Column Before
              </button>
              <button onClick={() => handleAction(() => editor.chain().focus().deleteColumn().run())}>
                Delete Column
              </button>
              <button onClick={() => handleAction(() => editor.chain().focus().addRowAfter().run())}>
                Add Row After
              </button>
              <button onClick={() => handleAction(() => editor.chain().focus().addRowBefore().run())}>
                Add Row Before
              </button>
              <button onClick={() => handleAction(() => editor.chain().focus().deleteRow().run())}>
                Delete Row
              </button>
              <button onClick={() => handleAction(() => editor.chain().focus().deleteTable().run())}>
                Delete Table
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const EditorToolbar = ({ editor }) => {
  if (!editor) return null;

  return (
    <div className="clinical-editor__toolbar">
      {/* Text formatting */}
      <div className="clinical-editor__toolbar-group">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive("bold")}
          title="Bold (Ctrl+B)"
        >
          <BoldIcon />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive("italic")}
          title="Italic (Ctrl+I)"
        >
          <ItalicIcon />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive("underline")}
          title="Underline (Ctrl+U)"
        >
          <UnderlineIcon />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          disabled={!editor.can().toggleStrike()}
          isActive={editor.isActive("strike")}
          title="Strikethrough"
        >
          <StrikethroughIcon />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          isActive={editor.isActive("highlight")}
          title="Highlight"
        >
          <HighlightIcon />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleSubscript().run()}
          isActive={editor.isActive("subscript")}
          title="Subscript"
        >
          <SubscriptIcon />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleSuperscript().run()}
          isActive={editor.isActive("superscript")}
          title="Superscript"
        >
          <SuperscriptIcon />
        </ToolbarButton>
      </div>

      <div className="clinical-editor__toolbar-divider" />

      {/* Headings */}
      <div className="clinical-editor__toolbar-group">
        <ToolbarButton
          onClick={() => applyHeadingLevel(editor, 1)}
          isActive={isHeadingLevelActive(editor, 1)}
          title="Heading 1"
        >
          <strong>H1</strong>
        </ToolbarButton>
        <ToolbarButton
          onClick={() => applyHeadingLevel(editor, 2)}
          isActive={isHeadingLevelActive(editor, 2)}
          title="Heading 2"
        >
          <strong>H2</strong>
        </ToolbarButton>
        <ToolbarButton
          onClick={() => applyHeadingLevel(editor, 3)}
          isActive={isHeadingLevelActive(editor, 3)}
          title="Heading 3"
        >
          <strong>H3</strong>
        </ToolbarButton>
      </div>

      <div className="clinical-editor__toolbar-divider" />

      {/* Alignment */}
      <div className="clinical-editor__toolbar-group">
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          isActive={editor.isActive({ textAlign: "left" })}
          title="Align Left"
        >
          <AlignLeftIcon />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          isActive={editor.isActive({ textAlign: "center" })}
          title="Align Center"
        >
          <AlignCenterIcon />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          isActive={editor.isActive({ textAlign: "right" })}
          title="Align Right"
        >
          <AlignRightIcon />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign("justify").run()}
          isActive={editor.isActive({ textAlign: "justify" })}
          title="Justify"
        >
          <AlignJustifyIcon />
        </ToolbarButton>
      </div>

      <div className="clinical-editor__toolbar-divider" />

      {/* Lists */}
      <div className="clinical-editor__toolbar-group">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive("bulletList")}
          title="Bullet List"
        >
          <BulletListIcon />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive("orderedList")}
          title="Numbered List"
        >
          <OrderedListIcon />
        </ToolbarButton>
      </div>

      <div className="clinical-editor__toolbar-divider" />

      {/* Indent / Outdent (using list sink/lift) */}
      <div className="clinical-editor__toolbar-group">
        <ToolbarButton
          onClick={() => editor.chain().focus().sinkListItem("listItem").run()}
          disabled={!editor.can().sinkListItem("listItem")}
          title="Indent"
        >
          <IndentIcon />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().liftListItem("listItem").run()}
          disabled={!editor.can().liftListItem("listItem")}
          title="Outdent"
        >
          <OutdentIcon />
        </ToolbarButton>
      </div>

      <div className="clinical-editor__toolbar-divider" />

      {/* Table */}
      <div className="clinical-editor__toolbar-group">
        <TableMenu editor={editor} />
      </div>

      {/* Page Break & Horizontal Rule */}
      <div className="clinical-editor__toolbar-group">
        <ToolbarButton
          onClick={() => editor.chain().focus().insertContent('<div class="html2pdf__page-break page-break" style="page-break-before:always;break-before:page;"><hr class="page-break-line" style="border:none;border-top:2px dashed #9ca3af;margin:18px 0;" /></div>').run()}
          title="Insert Page Break"
        >
          <PageBreakIcon />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Horizontal Rule"
        >
          <HrIcon />
        </ToolbarButton>
      </div>

      <div className="clinical-editor__toolbar-divider" />

      {/* Undo / Redo */}
      <div className="clinical-editor__toolbar-group">
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo (Ctrl+Z)"
        >
          <UndoIcon />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo (Ctrl+Y)"
        >
          <RedoIcon />
        </ToolbarButton>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════
   ClinicalEditor Component
   ═══════════════════════════════════════════════════════════════════════

   Props:
     value       — HTML string to initialise the editor with
     onChange     — called on every edit with the current HTML string
     onBlur      — called when the editor loses focus with the current HTML
     height      — editor content area height in px (default 400)
     placeholder — placeholder text when editor is empty
     readOnly    — if true, editor is not editable

   Ref:
     Exposes { getHTML(), getEditor() } for imperative access.
   ═══════════════════════════════════════════════════════════════════════ */

const ClinicalEditor = forwardRef(
  ({ value = "", onChange, onBlur, height = 400, placeholder = "", readOnly = false }, ref) => {
    // Track whether the content was set externally to avoid update loops
    const isExternalUpdate = useRef(false);

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: { levels: [1, 2, 3] },
          history: true,
        }),
        Underline,
        Table.configure({
          resizable: true,
          HTMLAttributes: { class: "clinical-table" },
        }),
        TableRow,
        TableCell,
        TableHeader,
        Placeholder.configure({
          placeholder: placeholder,
        }),
        TextAlign.configure({
          types: ["heading", "paragraph"],
        }),
        Superscript,
        Subscript,
        Highlight,
        InlineHeading,
        DivNode,
        SpanNode,
      ],
      content: value,
      editable: !readOnly,
      onUpdate: ({ editor: ed }) => {
        if (isExternalUpdate.current) return;
        if (onChange) {
          onChange(ed.getHTML());
        }
      },
      onBlur: ({ editor: ed }) => {
        if (onBlur) {
          onBlur(ed.getHTML());
        }
      },
    });

    // Sync external value changes into the editor
    useEffect(() => {
      if (!editor || value === undefined) return;
      // While the editor is focused, an incoming `value` is just the echo of our
      // own onChange — re-applying it would reset the caret mid-typing. Only sync
      // genuine external changes (template load, draft resume), which happen while
      // the editor is not focused.
      if (editor.isFocused) return;
      const currentHTML = editor.getHTML();
      // Only update if the incoming value is materially different
      // (avoid cursor-position-resetting loops)
      if (value !== currentHTML) {
        isExternalUpdate.current = true;
        editor.commands.setContent(value, false);
        isExternalUpdate.current = false;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    // Sync readOnly prop
    useEffect(() => {
      if (editor) {
        editor.setEditable(!readOnly);
      }
    }, [readOnly, editor]);

    // Expose imperative API
    useImperativeHandle(
      ref,
      () => ({
        getHTML: () => (editor ? editor.getHTML() : ""),
        getEditor: () => editor,
      }),
      [editor]
    );

    const contentStyle = {
      height: typeof height === "number" ? `${height}px` : height,
    };

    return (
      <div className="clinical-editor" style={contentStyle}>
        {!readOnly && <EditorToolbar editor={editor} />}
        <div className="clinical-editor__content">
          <EditorContent editor={editor} />
        </div>
      </div>
    );
  }
);

ClinicalEditor.displayName = "ClinicalEditor";

export default ClinicalEditor;
